import React, { useState, useRef, useCallback, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Highlighter,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Check,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SourceImage {
  id: number;
  filename: string;
  originalName: string;
  url: string;
  isPrimary: boolean;
}

export interface ImageHighlight {
  id: number;
  recipeId: number;
  imageId: number;
  x: number;
  y: number;
  width: number;
  height: number;
  dataType: string;
  extractedText: string | null;
  applied: boolean;
  createdAt: string | null;
}

interface DrawState {
  startX: number;
  startY: number;
  curX: number;
  curY: number;
}

interface RectPct {
  x: number;
  y: number;
  width: number;
  height: number;
}

type PopoverPhase = "select" | "extracting" | "review" | "edit";

interface ImageViewerProps {
  images: SourceImage[];
  recipeId: number | null;
  onApplyExtraction: (
    dataType: string,
    parsed: Record<string, unknown>,
    highlightId: number
  ) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DATA_TYPES = [
  { value: "notes", label: "Handwritten Notes" },
  { value: "ingredients", label: "Ingredients" },
  { value: "directions", label: "Directions" },
  { value: "title", label: "Title" },
  { value: "author", label: "Author/Source" },
  { value: "yield", label: "Yield/Servings" },
  { value: "times", label: "Prep/Cook Times" },
  { value: "other", label: "Other" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getBaseUrl(): string {
  return (import.meta.env.BASE_URL ?? "").replace(/\/$/, "");
}

function getDisplayText(dataType: string, extractedText: string): string {
  try {
    const p = JSON.parse(extractedText);
    switch (dataType) {
      case "notes":
      case "other":
        return p.text ?? extractedText;
      case "title":
        return p.title ?? extractedText;
      case "author":
        return p.author_source ?? extractedText;
      case "yield":
        return p.yield_servings ?? extractedText;
      case "directions":
        return p.directions ?? extractedText;
      case "ingredients": {
        const ings: Array<{ amount?: string; unit?: string; item?: string }> =
          p.ingredients ?? [];
        if (!ings.length) return extractedText;
        return ings
          .map((i) => [i.amount, i.unit, i.item].filter(Boolean).join(" "))
          .join("\n");
      }
      case "times": {
        const parts: string[] = [];
        if (p.prep_time) parts.push(`Prep: ${p.prep_time}`);
        if (p.cook_time) parts.push(`Cook: ${p.cook_time}`);
        if (p.total_time) parts.push(`Total: ${p.total_time}`);
        return parts.join(" · ") || extractedText;
      }
      default:
        return p.text ?? extractedText;
    }
  } catch {
    return extractedText;
  }
}

function getEditableText(dataType: string, extractedText: string): string {
  try {
    const p = JSON.parse(extractedText);
    switch (dataType) {
      case "notes":
      case "other":
        return p.text ?? extractedText;
      case "title":
        return p.title ?? extractedText;
      case "author":
        return p.author_source ?? extractedText;
      case "yield":
        return p.yield_servings ?? extractedText;
      case "directions":
        return p.directions ?? extractedText;
      case "ingredients": {
        const ings: Array<{
          amount?: string;
          unit?: string;
          item?: string;
          prep_notes?: string;
        }> = p.ingredients ?? [];
        return ings
          .map((i) =>
            [i.amount, i.unit, i.item, i.prep_notes ? `(${i.prep_notes})` : ""]
              .filter(Boolean)
              .join(" ")
          )
          .join("\n");
      }
      case "times": {
        const parts: string[] = [];
        if (p.prep_time) parts.push(`prep: ${p.prep_time}`);
        if (p.cook_time) parts.push(`cook: ${p.cook_time}`);
        if (p.total_time) parts.push(`total: ${p.total_time}`);
        return parts.join("\n");
      }
      default:
        return p.text ?? extractedText;
    }
  } catch {
    return extractedText;
  }
}

function buildApplyPayload(
  dataType: string,
  editedText: string,
  originalJson: string
): Record<string, unknown> {
  const lines = editedText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  switch (dataType) {
    case "notes":
    case "other":
      return { text: editedText };
    case "title":
      return { title: editedText };
    case "author":
      return { author_source: editedText };
    case "yield":
      return { yield_servings: editedText };
    case "directions":
      return { directions: editedText };
    case "ingredients": {
      try {
        const orig = JSON.parse(originalJson).ingredients ?? [];
        if (orig.length === lines.length) return { ingredients: orig };
      } catch {
        /* fallthrough */
      }
      return {
        ingredients: lines.map((line) => ({
          amount: null,
          unit: null,
          item: line,
          prep_notes: null,
          section_header: null,
        })),
      };
    }
    case "times": {
      const result: Record<string, string | null> = {
        prep_time: null,
        cook_time: null,
        total_time: null,
      };
      for (const line of lines) {
        const lower = line.toLowerCase();
        if (lower.startsWith("prep:"))
          result.prep_time = line.replace(/^prep:\s*/i, "").trim();
        else if (lower.startsWith("cook:"))
          result.cook_time = line.replace(/^cook:\s*/i, "").trim();
        else if (lower.startsWith("total:"))
          result.total_time = line.replace(/^total:\s*/i, "").trim();
      }
      return result;
    }
    default:
      return { text: editedText };
  }
}

// ─── Popover ──────────────────────────────────────────────────────────────────

interface PopoverProps {
  phase: PopoverPhase;
  dataType: string;
  extractedText: string | null;
  editText: string;
  pos: { x: number; y: number };
  isExistingHighlight: boolean;
  isApplied: boolean;
  onDataTypeChange: (v: string) => void;
  onExtract: () => void;
  onApply: () => void;
  onEdit: () => void;
  onApplyEdit: () => void;
  onCancelEdit: () => void;
  onEditTextChange: (v: string) => void;
  onDiscard: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function HighlightPopover({
  phase,
  dataType,
  extractedText,
  editText,
  pos,
  isExistingHighlight,
  isApplied,
  onDataTypeChange,
  onExtract,
  onApply,
  onEdit,
  onApplyEdit,
  onCancelEdit,
  onEditTextChange,
  onDiscard,
  onDelete,
  onClose,
}: PopoverProps) {
  const displayText =
    extractedText ? getDisplayText(dataType, extractedText) : "";
  const dataTypeLabel =
    DATA_TYPES.find((d) => d.value === dataType)?.label ?? dataType;

  return (
    <div
      className="fixed z-50 w-72 rounded-xl overflow-hidden"
      style={{
        left: pos.x,
        top: pos.y,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-orange-50 border-b border-orange-100">
        <span className="text-xs font-semibold text-orange-800">
          {phase === "extracting"
            ? "Processing…"
            : phase === "edit"
            ? "Edit extraction"
            : isExistingHighlight
            ? `${dataTypeLabel}${isApplied ? " ✓" : ""}`
            : "Highlight & Extract"}
        </span>
        <button
          onClick={onClose}
          className="text-orange-400 hover:text-orange-700 rounded p-0.5"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* SELECT phase */}
        {phase === "select" && (
          <>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">
                What is this?
              </label>
              <Select value={dataType} onValueChange={onDataTypeChange}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_TYPES.map((dt) => (
                    <SelectItem key={dt.value} value={dt.value} className="text-xs">
                      {dt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white border-0"
                onClick={onExtract}
              >
                Process Data
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={onDiscard}
              >
                Cancel
              </Button>
            </div>
            {isExistingHighlight && (
              <button
                onClick={onDelete}
                className="flex items-center gap-1 text-xs text-destructive hover:underline"
              >
                <Trash2 className="h-3 w-3" /> Delete highlight
              </button>
            )}
          </>
        )}

        {/* EXTRACTING phase */}
        {phase === "extracting" && (
          <div className="flex flex-col items-center gap-2 py-3">
            <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            <p className="text-xs text-muted-foreground">
              Analysing selected region…
            </p>
          </div>
        )}

        {/* REVIEW phase */}
        {phase === "review" && extractedText && (
          <>
            <div className="bg-muted/50 rounded-lg p-2.5 max-h-36 overflow-y-auto">
              <p className="text-xs whitespace-pre-wrap break-words text-foreground leading-relaxed">
                {displayText}
              </p>
            </div>
            {!isApplied ? (
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700 text-white border-0"
                  onClick={onApply}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={onEdit}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/5"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-xs text-green-600 font-medium">
                  ✓ Applied to recipe
                </span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={onEdit}
                  >
                    Re-apply
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-destructive"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* EDIT phase */}
        {phase === "edit" && (
          <>
            <textarea
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)}
              className="w-full min-h-[80px] text-xs border rounded-lg p-2 resize-y font-mono focus:outline-none focus:ring-1 focus:ring-orange-400 bg-background"
              placeholder="Edit extracted text…"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700 text-white border-0"
                onClick={onApplyEdit}
              >
                <Check className="h-3 w-3 mr-1" />
                Apply
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={onCancelEdit}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function ImageViewer({
  images,
  recipeId,
  onApplyExtraction,
}: ImageViewerProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [highlightMode, setHighlightMode] = useState(false);
  const [drawing, setDrawing] = useState<DrawState | null>(null);
  const [pendingRect, setPendingRect] = useState<RectPct | null>(null);
  const [highlights, setHighlights] = useState<ImageHighlight[]>([]);
  const [selectedHighlight, setSelectedHighlight] =
    useState<ImageHighlight | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [popoverPhase, setPopoverPhase] = useState<PopoverPhase>("select");
  const [selectedDataType, setSelectedDataType] = useState("notes");
  const [editText, setEditText] = useState("");

  const transformRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const fitScaleRef = useRef(0.9);

  const current = images[Math.min(activeIdx, images.length - 1)];
  const currentHighlights = highlights.filter(
    (h) => current && h.imageId === current.id
  );

  // ── Load highlights whenever recipe changes
  useEffect(() => {
    if (!recipeId) {
      setHighlights([]);
      return;
    }
    const base = getBaseUrl();
    fetch(`${base}/api/highlights?recipeId=${recipeId}`)
      .then((r) => r.json())
      .then((d) => setHighlights(d.highlights ?? []))
      .catch(() => {});
  }, [recipeId]);

  // ── Reset draw state when switching pages
  useEffect(() => {
    setDrawing(null);
    setPendingRect(null);
    setSelectedHighlight(null);
    setPopoverPos(null);
  }, [activeIdx]);

  // ── Compute popover anchor based on overlay + rectangle percentages
  const computePopoverPos = useCallback(
    (rect: RectPct): { x: number; y: number } | null => {
      const overlayEl = overlayRef.current;
      if (!overlayEl) return null;
      const br = overlayEl.getBoundingClientRect();
      const px = br.left + ((rect.x + rect.width) / 100) * br.width + 12;
      const py = br.top + (rect.y / 100) * br.height;
      return {
        x: Math.min(px, window.innerWidth - 300),
        y: Math.max(8, Math.min(py, window.innerHeight - 340)),
      };
    },
    []
  );

  // ── Fit to extents on image load
  const handleImgLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (!containerRef.current) return;
      const containerW = containerRef.current.clientWidth;
      const containerH = containerRef.current.clientHeight;
      const scaleX = (containerW - 32) / img.naturalWidth;
      const scaleY = (containerH - 32) / img.naturalHeight;
      const scale = Math.min(scaleX, scaleY, 1);
      fitScaleRef.current = scale;
      // Slight delay so TransformWrapper finishes its own init first
      requestAnimationFrame(() => {
        transformRef.current?.centerView(scale, 0);
      });
    },
    []
  );

  // ── Overlay coordinate helper (% of image)
  const getOverlayPct = (
    e: React.MouseEvent<HTMLDivElement>
  ): { x: number; y: number } => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    return { x, y };
  };

  // ── Mouse events on overlay
  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!highlightMode) return;
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = getOverlayPct(e);
    setDrawing({ startX: x, startY: y, curX: x, curY: y });
    setPendingRect(null);
    setSelectedHighlight(null);
    setPopoverPos(null);
  };

  const handleOverlayMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getOverlayPct(e);
    setDrawing((d) => (d ? { ...d, curX: x, curY: y } : null));
  };

  const handleOverlayMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getOverlayPct(e);
    const rx = Math.min(drawing.startX, x);
    const ry = Math.min(drawing.startY, y);
    const rw = Math.abs(x - drawing.startX);
    const rh = Math.abs(y - drawing.startY);
    setDrawing(null);
    if (rw > 1 && rh > 1) {
      const rect = { x: rx, y: ry, width: rw, height: rh };
      setPendingRect(rect);
      setPopoverPhase("select");
      setSelectedHighlight(null);
      // Position popover after the overlay has updated
      requestAnimationFrame(() => {
        const pos = computePopoverPos(rect);
        setPopoverPos(pos);
      });
    }
  };

  // ── Click existing highlight
  const handleHighlightClick = (h: ImageHighlight, e: React.MouseEvent) => {
    e.stopPropagation();
    if (drawing) return;
    setSelectedHighlight(h);
    setPendingRect(null);
    setPopoverPhase(h.extractedText ? "review" : "select");
    setSelectedDataType(h.dataType);
    if (h.extractedText) {
      setEditText(getEditableText(h.dataType, h.extractedText));
    }
    requestAnimationFrame(() => {
      const pos = computePopoverPos({
        x: h.x,
        y: h.y,
        width: h.width,
        height: h.height,
      });
      setPopoverPos(pos);
    });
  };

  // ── Send to Claude
  const handleExtract = async () => {
    if (!current || !recipeId) return;
    const rect = pendingRect ??
      (selectedHighlight
        ? {
            x: selectedHighlight.x,
            y: selectedHighlight.y,
            width: selectedHighlight.width,
            height: selectedHighlight.height,
          }
        : null);
    if (!rect) return;

    const base = getBaseUrl();
    setPopoverPhase("extracting");
    try {
      const res = await fetch(`${base}/api/extract-region`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipeId,
          imageId: current.id,
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          dataType: selectedDataType,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Extraction failed");
      const newH: ImageHighlight = data.highlight;
      setHighlights((prev) => [...prev, newH]);
      setSelectedHighlight(newH);
      setPendingRect(null);
      setEditText(getEditableText(newH.dataType, newH.extractedText ?? ""));
      setPopoverPhase("review");
      requestAnimationFrame(() => {
        const pos = computePopoverPos({
          x: newH.x,
          y: newH.y,
          width: newH.width,
          height: newH.height,
        });
        setPopoverPos(pos);
      });
    } catch (err) {
      console.error(err);
      setPopoverPhase("select");
    }
  };

  // ── Apply to recipe
  const applyHighlight = async (overrideText?: string) => {
    if (!selectedHighlight) return;
    const base = getBaseUrl();
    const sourceJson = selectedHighlight.extractedText ?? "{}";
    const payload =
      overrideText != null
        ? buildApplyPayload(selectedHighlight.dataType, overrideText, sourceJson)
        : (() => {
            try {
              return JSON.parse(sourceJson) as Record<string, unknown>;
            } catch {
              return { text: sourceJson };
            }
          })();

    onApplyExtraction(selectedHighlight.dataType, payload, selectedHighlight.id);

    // Mark applied in DB
    await fetch(`${base}/api/highlights/${selectedHighlight.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applied: true }),
    }).catch(() => {});

    setHighlights((prev) =>
      prev.map((h) =>
        h.id === selectedHighlight.id ? { ...h, applied: true } : h
      )
    );
    setSelectedHighlight((h) => (h ? { ...h, applied: true } : null));
    setPopoverPhase("review");
  };

  // ── Delete highlight
  const deleteHighlight = async () => {
    if (!selectedHighlight) return;
    const base = getBaseUrl();
    await fetch(`${base}/api/highlights/${selectedHighlight.id}`, {
      method: "DELETE",
    }).catch(() => {});
    setHighlights((prev) => prev.filter((h) => h.id !== selectedHighlight.id));
    setSelectedHighlight(null);
    setPopoverPos(null);
    setPendingRect(null);
  };

  // ── Close popover
  const closePopover = () => {
    setPendingRect(null);
    setSelectedHighlight(null);
    setPopoverPos(null);
  };

  if (images.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
        No source images found
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" ref={containerRef}>
      {/* Page tabs */}
      {images.length > 1 && (
        <div className="flex items-center gap-1 px-3 py-2 border-b bg-background overflow-x-auto flex-shrink-0">
          {images.map((img, i) => (
            <button
              key={img.id}
              onClick={() => setActiveIdx(i)}
              className={cn(
                "px-3 py-1 text-xs rounded-md font-medium transition-colors whitespace-nowrap",
                i === activeIdx
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted text-muted-foreground"
              )}
            >
              Page {i + 1}
              {img.isPrimary && " ★"}
            </button>
          ))}
        </div>
      )}

      {/* Viewer */}
      <div className="relative flex-1 overflow-hidden bg-stone-100">
        <TransformWrapper
          ref={transformRef}
          key={`${current.id}-${activeIdx}`}
          initialScale={0.9}
          minScale={0.05}
          maxScale={10}
          centerOnInit
          limitToBounds={false}
          panning={{ disabled: highlightMode }}
        >
          {({ zoomIn, zoomOut }) => (
            <>
              {/* Toolbar */}
              <div className="absolute top-3 right-3 z-20 flex flex-col gap-1">
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 shadow-sm"
                  onClick={() =>
                    transformRef.current?.centerView(fitScaleRef.current, 200)
                  }
                  title="Fit to view"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 shadow-sm"
                  onClick={() => zoomIn()}
                  title="Zoom in"
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 shadow-sm"
                  onClick={() => zoomOut()}
                  title="Zoom out"
                >
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant={highlightMode ? "default" : "secondary"}
                  className={cn(
                    "h-7 w-7 shadow-sm",
                    highlightMode &&
                      "bg-orange-500 hover:bg-orange-600 text-white border-0"
                  )}
                  onClick={() => {
                    setHighlightMode((m) => !m);
                    setDrawing(null);
                    setPendingRect(null);
                    setPopoverPos(null);
                    setSelectedHighlight(null);
                  }}
                  title={
                    highlightMode ? "Exit highlight mode" : "Highlight & Extract"
                  }
                >
                  <Highlighter className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Instruction badge */}
              {highlightMode && !drawing && !pendingRect && !popoverPos && (
                <div className="absolute top-3 left-3 z-20 bg-orange-500 text-white text-xs px-2.5 py-1.5 rounded-lg shadow pointer-events-none select-none">
                  Draw a rectangle to extract text
                </div>
              )}

              {/* Multi-page navigation arrows */}
              {images.length > 1 && (
                <>
                  {activeIdx > 0 && (
                    <button
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background border rounded-full p-1 shadow-sm"
                      onClick={() => setActiveIdx((i) => i - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                  {activeIdx < images.length - 1 && (
                    <button
                      className="absolute right-12 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background border rounded-full p-1 shadow-sm"
                      onClick={() => setActiveIdx((i) => i + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}

              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="relative" style={{ display: "inline-block" }}>
                  <img
                    src={current.url}
                    alt={current.originalName}
                    className="max-w-none shadow-lg block"
                    style={{ userSelect: "none" }}
                    draggable={false}
                    onLoad={handleImgLoad}
                  />

                  {/* Highlight overlay (same size as image, in transform-space) */}
                  <div
                    ref={overlayRef}
                    className="absolute inset-0"
                    style={{ cursor: highlightMode ? "crosshair" : "default" }}
                    onMouseDown={handleOverlayMouseDown}
                    onMouseMove={handleOverlayMouseMove}
                    onMouseUp={handleOverlayMouseUp}
                  >
                    {/* Persisted highlights */}
                    {currentHighlights.map((h) => (
                      <div
                        key={h.id}
                        style={{
                          position: "absolute",
                          left: `${h.x}%`,
                          top: `${h.y}%`,
                          width: `${h.width}%`,
                          height: `${h.height}%`,
                          backgroundColor: h.applied
                            ? "rgba(34,197,94,0.18)"
                            : "rgba(251,191,36,0.25)",
                          border: `2px solid ${h.applied ? "#16a34a" : "#f59e0b"}`,
                          boxSizing: "border-box",
                          borderRadius: "2px",
                          cursor:
                            highlightMode ? "crosshair" : "pointer",
                          zIndex: 1,
                        }}
                        onClick={(e) => {
                          if (!highlightMode) handleHighlightClick(h, e);
                        }}
                      />
                    ))}

                    {/* Selected highlight indicator */}
                    {selectedHighlight &&
                      selectedHighlight.imageId === current.id && (
                        <div
                          style={{
                            position: "absolute",
                            left: `${selectedHighlight.x}%`,
                            top: `${selectedHighlight.y}%`,
                            width: `${selectedHighlight.width}%`,
                            height: `${selectedHighlight.height}%`,
                            border: "2px solid #f97316",
                            boxSizing: "border-box",
                            borderRadius: "2px",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        />
                      )}

                    {/* In-progress drawing */}
                    {drawing && (
                      <div
                        style={{
                          position: "absolute",
                          left: `${Math.min(drawing.startX, drawing.curX)}%`,
                          top: `${Math.min(drawing.startY, drawing.curY)}%`,
                          width: `${Math.abs(drawing.curX - drawing.startX)}%`,
                          height: `${Math.abs(drawing.curY - drawing.startY)}%`,
                          backgroundColor: "rgba(249,115,22,0.2)",
                          border: "2px solid #f97316",
                          boxSizing: "border-box",
                          pointerEvents: "none",
                          borderRadius: "2px",
                          zIndex: 3,
                        }}
                      />
                    )}

                    {/* Pending rect (after draw, before send) */}
                    {pendingRect && (
                      <div
                        style={{
                          position: "absolute",
                          left: `${pendingRect.x}%`,
                          top: `${pendingRect.y}%`,
                          width: `${pendingRect.width}%`,
                          height: `${pendingRect.height}%`,
                          backgroundColor: "rgba(249,115,22,0.15)",
                          border: "2px dashed #f97316",
                          boxSizing: "border-box",
                          pointerEvents: "none",
                          borderRadius: "2px",
                          zIndex: 3,
                        }}
                      />
                    )}
                  </div>
                </div>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t bg-background flex-shrink-0">
        <p
          className="text-xs text-muted-foreground truncate"
          title={current.originalName}
        >
          {current.originalName}
          {images.length > 1 && ` (${activeIdx + 1} / ${images.length})`}
          {highlightMode && (
            <span className="ml-2 text-orange-500 font-medium">
              ● Highlight mode
            </span>
          )}
        </p>
      </div>

      {/* Popover — fixed position, outside transform */}
      {popoverPos && (
        <HighlightPopover
          phase={popoverPhase}
          dataType={
            selectedHighlight ? selectedHighlight.dataType : selectedDataType
          }
          extractedText={selectedHighlight?.extractedText ?? null}
          editText={editText}
          pos={popoverPos}
          isExistingHighlight={selectedHighlight != null}
          isApplied={selectedHighlight?.applied ?? false}
          onDataTypeChange={setSelectedDataType}
          onExtract={handleExtract}
          onApply={() => applyHighlight()}
          onEdit={() => {
            if (selectedHighlight?.extractedText) {
              setEditText(
                getEditableText(
                  selectedHighlight.dataType,
                  selectedHighlight.extractedText
                )
              );
            }
            setPopoverPhase("edit");
          }}
          onApplyEdit={() => applyHighlight(editText)}
          onCancelEdit={() => setPopoverPhase("review")}
          onEditTextChange={setEditText}
          onDiscard={closePopover}
          onDelete={deleteHighlight}
          onClose={closePopover}
        />
      )}
    </div>
  );
}
